import * as React from 'react';
import { ArrayParam, NumberParam, useQueryParams } from 'use-query-params';

import Color from 'src/assets/_util.scss';
import {
  getActiveCollections,
  handleCollectionChange,
} from 'src/components/card-filters/CollectionFilter';
import FilterEmptyState from 'src/components/card-filters/FilterEmptyState';
import {
  getActivePrivacy,
  handlePrivacyChange,
} from 'src/components/card-filters/PrivacyFilter';
import {
  getActiveTags,
  handleTagChange,
} from 'src/components/card-filters/TagFilter';
import LoadingSpinner from 'src/components/core/LoadingSpinner/LoadingSpinner';
import { VStack } from 'src/components/core/Stack';
import FilterTags from 'src/components/FilterTags/FilterTags';
import HighlightCard from 'src/components/HighlightCard/HighlightCard';
import styles from 'src/components/HighlightsRoute/HighlightsRoute.module.scss';
import PagingControl from 'src/components/PagingControl/PagingControl';
import PagingSummary from 'src/components/PagingSummary/PagingSummary';
import { Highlight, Paging } from 'src/types/conversation';
import { HighlightsFilterOptions } from 'src/types/core';
import { makePagingHandlers } from 'src/util/paging';

interface BaseProps {
  currentPage: number;
  highlights: Highlight[];
  pagingInfo: Paging | undefined;
  filterOptions: HighlightsFilterOptions | undefined;
  emphasizedTerms?: string[];
  reloadTab?: () => void;
  noneFoundMsg: React.ReactNode;
  isLoading: boolean;
  noneExistMsg?: React.ReactNode;
}

type Props = BaseProps;

const PAGE_SIZE = 10;

const HighlightsTabPane = ({
  currentPage,
  highlights,
  pagingInfo,
  emphasizedTerms,
  filterOptions,
  reloadTab,
  isLoading,
  noneFoundMsg,
  noneExistMsg,
}: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    c: ArrayParam,
    tags: ArrayParam,
    priv: ArrayParam,
  });
  const { c: collectionIds, tags, priv: privacyLevels } = query;

  const { handleNextPage, handlePrevPage, handleSeekPage } = makePagingHandlers(
    currentPage,
    PAGE_SIZE,
    pagingInfo ? pagingInfo.total : 0,
    (page: number) => setQuery({ page }, 'pushIn')
  );

  const clearAllFilters = () => {
    setQuery({
      c: undefined,
      tags: undefined,
      priv: undefined,
    });
  };

  const activeCollections = getActiveCollections(collectionIds, filterOptions);
  const activeTags = getActiveTags(tags, filterOptions);
  const activePrivacy = getActivePrivacy(privacyLevels, filterOptions);
  const filtersActive =
    collectionIds != null || tags != null || privacyLevels != null;

  const hasHighlights = highlights.length > 0;
  const ready = !isLoading && hasHighlights;

  return (
    <VStack
      className={styles.highlightCardsContainer}
      data-testid="highlight-tab-pane"
    >
      {filtersActive && (
        <div className="mb-2 px-3">
          <FilterTags
            activeFilterOptions={activeCollections}
            onClear={(option) =>
              handleCollectionChange(option, collectionIds, setQuery)
            }
          />
          <FilterTags
            activeFilterOptions={activeTags}
            onClear={(option) => handleTagChange(option, tags, setQuery)}
          />
          <FilterTags
            activeFilterOptions={activePrivacy}
            onClear={(option) =>
              handlePrivacyChange(option, privacyLevels, setQuery)
            }
          />
        </div>
      )}

      {ready && (
        <PagingSummary
          className="text-gray-700 px-3"
          itemsPerPage={PAGE_SIZE}
          pagingInfo={pagingInfo}
          i18NextKey="highlights.showing"
          i18NextRangeKey="highlights.showing_range"
        />
      )}

      <VStack sx={{ overflow: 'auto', py: 2, px: 4 }}>
        {isLoading ? (
          <div className="w-100 text-center mt-4">
            <LoadingSpinner active size="lg" />
          </div>
        ) : (
          highlights.map((highlight) => (
            <HighlightCard
              key={highlight.id}
              className={`mb-4 ${styles.card}`}
              highlight={highlight}
              emphasizedTerms={emphasizedTerms}
              onDelete={reloadTab}
            />
          ))
        )}
      </VStack>

      {!isLoading && !hasHighlights && (
        <FilterEmptyState
          onClear={clearAllFilters}
          filtersActive={filtersActive}
          title={noneFoundMsg}
          noneExistMsg={noneExistMsg}
          className="mt-3"
        />
      )}

      {ready && (
        <PagingControl
          className={'mb-5 text-center w-100'}
          itemsPerPage={PAGE_SIZE}
          pagingInfo={pagingInfo}
          onNext={handleNextPage}
          onPrevious={handlePrevPage}
          onSeek={handleSeekPage}
          style={{
            borderTop: `1px solid ${Color.gray400}`,
            width: '100%',
          }}
        />
      )}
    </VStack>
  );
};

export default HighlightsTabPane;
